import { useCallback, useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
  startAfter,
  getDocs,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Post } from "@/lib/types";

const PAGE_SIZE = 20;

export function usePosts(clubId: string | undefined) {
  const [posts, setPosts] = useState<(Post & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Track first page docs separately so live updates only replace page 1
  const firstPageRef = useRef<(Post & { id: string })[]>([]);
  const olderPagesRef = useRef<(Post & { id: string })[]>([]);
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);

  // Live listener for the first page
  useEffect(() => {
    if (!clubId) {
      setPosts([]);
      setLoading(false);
      firstPageRef.current = [];
      olderPagesRef.current = [];
      lastDocRef.current = null;
      setHasMore(true);
      return;
    }

    olderPagesRef.current = [];
    lastDocRef.current = null;
    setHasMore(true);

    const q = query(
      collection(db, "clubs", clubId, "posts"),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Post) }));
      firstPageRef.current = items;
      lastDocRef.current = snap.docs[snap.docs.length - 1] ?? null;
      // Merge with older pages already loaded, dedupe by id
      const seen = new Set(items.map((p) => p.id));
      const merged = [...items, ...olderPagesRef.current.filter((p) => !seen.has(p.id))];
      setPosts(merged);
      setLoading(false);
      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
    });

    return unsub;
  }, [clubId]);

  const loadMore = useCallback(async () => {
    if (!clubId || loadingMore || !hasMore || !lastDocRef.current) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, "clubs", clubId, "posts"),
        orderBy("createdAt", "desc"),
        startAfter(lastDocRef.current),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Post) }));
      if (items.length === 0) {
        setHasMore(false);
        return;
      }
      lastDocRef.current = snap.docs[snap.docs.length - 1] ?? lastDocRef.current;
      olderPagesRef.current = [...olderPagesRef.current, ...items];
      const seen = new Set(firstPageRef.current.map((p) => p.id));
      setPosts([...firstPageRef.current, ...olderPagesRef.current.filter((p) => !seen.has(p.id))]);
      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [clubId, loadingMore, hasMore]);

  return { posts, loading, loadingMore, hasMore, loadMore };
}
